/**
 * kevia-ethics/index.js
 * Unified pastoral assessment entry point.
 * Combines all 5 ethics modules into a single assessment object
 * and generates a tiered system prompt block for injection.
 */

import { detectEmotionalSensitivity }  from "./emotionalSensitivityDetector";
import { detectPastoralRisk }          from "./pastoralEthicsLayer";
import { calculateConfidence }         from "./confidenceEngine";
import { buildResponseBoundaries }     from "./responseBoundaryEngine";
import { getDoctrinalCertaintyLevel }  from "./doctrinalCertaintyScale";

/**
 * @param {{
 *   text:               string,
 *   intent:             string,
 *   mood:               string | null,
 *   entityContext:      object | null,
 *   bibleQuery:         object | null,
 *   denominationalPlan: object | null,
 * }} params
 * @returns {{
 *   ethics:     object,
 *   emotion:    object,
 *   confidence: object,
 *   certainty:  object,
 *   boundaries: object,
 *   systemBlock: string,
 *   riskLevel:  string,
 * }}
 */
export function buildPastoralAssessment({
  text,
  intent,
  mood,
  entityContext,
  bibleQuery,
  denominationalPlan,
}) {
  // 1. Emotional state
  const emotion = detectEmotionalSensitivity(text);

  // 2. Risk layer (informed by emotion for intensity bump)
  const ethics = detectPastoralRisk(text, intent, emotion);

  // 3. Confidence scoring
  const confidence = calculateConfidence({
    text,
    intent,
    bibleQuery,
    denominationalPlan,
    ethics,
    entityContext,
  });

  // 4. Doctrinal certainty level
  const certainty = getDoctrinalCertaintyLevel({
    intent,
    bibleQuery,
    denominationalPlan,
    ethics,
  });

  // 5. Response boundaries (constraint set)
  const boundaries = buildResponseBoundaries({ ethics, confidence, certainty, emotion });

  // 6. Compose system prompt block
  const systemBlock = buildEthicsSystemBlock({ ethics, confidence, emotion, certainty, boundaries });

  return {
    ethics,
    emotion,
    confidence,
    certainty,
    boundaries,
    systemBlock,
    riskLevel: ethics.riskLevel,
  };
}

/**
 * Builds the system prompt block injected before the user message.
 * Risk-tiered: critical gets full safety block, lower risks get lighter guidance.
 *
 * @param {{ ethics, confidence, emotion, certainty, boundaries }} params
 * @returns {string}
 */
export function buildEthicsSystemBlock({ ethics, confidence, emotion, certainty, boundaries }) {
  const risk = ethics?.riskLevel ?? "none";

  if (risk === "none" && !boundaries?.active && !confidence?.shouldQualifyResponse) {
    return "";
  }

  const lines = [];

  // ── Critical block ───────────────────────────────────────────────
  if (risk === "critical") {
    lines.push("SEGURIDAD HUMANA PRIMERO: Esta conversación contiene indicadores de riesgo crítico.");
    lines.push("— Antes de cualquier respuesta pastoral, expresa cuidado directo y humano.");
    lines.push("— Orienta hacia recursos de crisis o apoyo profesional de manera cálida, no clínica.");
    lines.push("— No simplifiques el dolor con frases espirituales. Escucha antes de consolar.");
    if (ethics?.escalationRecommendation) {
      lines.push(`— ${ethics.escalationRecommendation}`);
    }
  }

  // ── High risk block ──────────────────────────────────────────────
  else if (risk === "high") {
    lines.push("SITUACIÓN PASTORAL SENSIBLE: Responde con cuidado y sin apresurar soluciones.");
    if (ethics?.escalationRecommendation) {
      lines.push(`— ${ethics.escalationRecommendation}`);
    }
    lines.push("— No reemplaces la necesidad de apoyo profesional o comunitario real.");
  }

  // ── Medium risk block ────────────────────────────────────────────
  else if (risk === "medium") {
    lines.push("CONTEXTO PASTORAL DELICADO: Usa tono cálido y evita afirmaciones absolutas.");
  }

  // ── Constraint lines ─────────────────────────────────────────────
  if (boundaries?.systemPromptLines?.length > 0) {
    lines.push(...boundaries.systemPromptLines);
  }

  // ── Confidence qualification ─────────────────────────────────────
  if (confidence?.shouldQualifyResponse && risk === "none") {
    lines.push("— Cuando no estés seguro, reconócelo con humildad. Prefiere preguntas clarificadoras.");
  }

  // ── Doctrinal certainty guidance ─────────────────────────────────
  if (certainty?.promptGuidance && certainty.level <= 3) {
    lines.push(`— ${certainty.promptGuidance}`);
  }

  if (lines.length === 0) return "";

  return `\n\n[ORIENTACIÓN ÉTICA PASTORAL]\n${lines.join("\n")}`;
}
